import React, { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GraduationCap, Sparkles } from "lucide-react";

/**
 * Ultra-Compact Premium Hub Pills
 * - 48px height, minimal design
 * - No arrow button - entire pill is clickable
 * - Ladies + Student hubs only on Home (booking removed per UX request)
 * - RTL support
 */

function hapticLight() {
  try {
    if (navigator?.vibrate) navigator.vibrate(10);
  } catch {}
}

export default function HubsMiniRow({ className = "" }) {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar') || document.dir === 'rtl';
  const lang = isRTL ? 'ar' : 'en';

  const hubs = useMemo(
    () => [
      {
        id: "ladies",
        label: { en: "Ladies", ar: "السيدات" },
        subtitle: { en: "Items & Services", ar: "منتجات وخدمات" },
        icon: <Sparkles size={15} />,
        to: "/ladies",
        theme: "orange",
      },
      {
        id: "student",
        label: { en: "Students", ar: "الطلاب" },
        subtitle: { en: "Books \u2022 CV", ar: "\u0643\u062a\u0628 \u2022 \u0633\u064a\u0631\u0629 \u0630\u0627\u062a\u064a\u0629" },
        icon: <GraduationCap size={15} />,
        to: "/student-hub",
        theme: "academic-blue", // Changed from emerald to academic-blue
      },
    ],
    []
  );

  const onHubClick = useCallback((hub) => {
    hapticLight();
    nav(hub.to);
  }, [nav]);

  return (
    <section
      className={`micro-hubs ${className}`}
      dir={isRTL ? "rtl" : "ltr"}
      aria-label="Explore hubs"
      data-testid="hubs-mini-row"
    >
      {/* Header */}
      <div className="micro-hubs__header">
        <span className="micro-hubs__title">
          {t('hmr.exploreHubs')}
        </span>
        <span className="micro-hubs__hint">
          {t('hmr.tapToBrowse')}
        </span>
      </div>
      
      {/* Hub pills */}
      <div className={`micro-hubs__row ${hubs.length <= 2 ? 'micro-hubs__row--compact' : ''}`} role="navigation">
        {hubs.map((hub) => {
          const label = hub.label[lang];
          const subtitle = hub.subtitle?.[lang];

          return (
            <button
              key={hub.id}
              type="button"
              className={`micro-pill micro-pill--${hub.theme}`}
              onClick={() => onHubClick(hub)}
              data-testid={`hub-card-${hub.id}`}
            >
              <div className="micro-pill__icon">
                {hub.icon}
              </div>
              <div className="micro-pill__text">
                <span className="micro-pill__label">{label}</span>
                <span className="micro-pill__sub">{subtitle}</span>
              </div>
            </button>
          );
        })}
      </div>

      <style>{`
        .micro-hubs {
          padding: 6px 12px 10px;
        }

        .micro-hubs__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 2px 6px;
        }

        .micro-hubs__title {
          font-size: 10px;
          font-weight: 700;
          color: rgba(17, 24, 39, 0.55);
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .micro-hubs__hint {
          font-size: 10px;
          font-weight: 500;
          color: rgba(17, 24, 39, 0.32);
        }

        html.dark .micro-hubs__title {
          color: rgba(255, 255, 255, 0.50);
        }
        html.dark .micro-hubs__hint {
          color: rgba(255, 255, 255, 0.28);
        }

        .micro-hubs__row {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .micro-hubs__row--compact {
          overflow-x: visible;
        }

        .micro-hubs__row::-webkit-scrollbar {
          display: none;
        }

        /* === MICRO PILL BASE === */
        .micro-pill {
          flex: 0 0 auto;
          min-width: 130px;
          max-width: 150px;
          height: 48px;
          padding: 0 10px;
          border-radius: 24px;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          transition: transform 0.1s ease, box-shadow 0.15s ease;
          scroll-snap-align: start;
          border: none;
          text-align: start;
        }

        .micro-hubs__row--compact .micro-pill {
          flex: 1;
          min-width: 0;
          max-width: none;
        }

        .micro-pill:active {
          transform: scale(0.97);
        }

        .micro-pill__icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          flex-shrink: 0;
          color: white;
        }

        .micro-pill__text {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          line-height: 1.15;
        }

        .micro-pill__label {
          font-size: 14px;
          font-weight: 700;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .micro-pill__sub {
          font-size: 12px;
          font-weight: 500;
          opacity: 0.6;
          white-space: nowrap;
        }

        /* === LADIES - ORANGE === */
        .micro-pill--orange {
          background: linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%);
          box-shadow: 0 1px 4px rgba(249, 115, 22, 0.10);
        }

        .micro-pill--orange:hover {
          box-shadow: 0 3px 10px rgba(249, 115, 22, 0.15);
        }

        .micro-pill--orange .micro-pill__icon {
          background: linear-gradient(135deg, #F97316 0%, #EA580C 100%);
          box-shadow: 0 2px 6px rgba(249, 115, 22, 0.25);
        }

        .micro-pill--orange .micro-pill__label {
          color: #9A3412;
        }

        .micro-pill--orange .micro-pill__sub {
          color: #C2410C;
        }

        html.dark .micro-pill--orange {
          background: linear-gradient(135deg, rgba(124, 45, 18, 0.45) 0%, rgba(154, 52, 18, 0.35) 100%);
        }

        html.dark .micro-pill--orange .micro-pill__label {
          color: #FDBA74;
        }

        html.dark .micro-pill--orange .micro-pill__sub {
          color: #FB923C;
        }

        /* === STUDENT - ACADEMIC BLUE (matches Student Hub identity) === */
        .micro-pill--academic-blue {
          background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
          box-shadow: 0 1px 4px rgba(59, 130, 246, 0.12);
        }

        .micro-pill--academic-blue:hover {
          box-shadow: 0 3px 10px rgba(59, 130, 246, 0.20);
        }

        .micro-pill--academic-blue .micro-pill__icon {
          background: linear-gradient(135deg, #2E3A8C 0%, #4F46E5 50%, #3B82F6 100%);
          box-shadow: 0 2px 6px rgba(59, 130, 246, 0.30);
        }

        .micro-pill--academic-blue .micro-pill__label {
          color: #1E3A8A;
        }

        .micro-pill--academic-blue .micro-pill__sub {
          color: #3B82F6;
        }

        html.dark .micro-pill--academic-blue {
          background: linear-gradient(135deg, rgba(30, 58, 138, 0.45) 0%, rgba(59, 130, 246, 0.35) 100%);
        }

        html.dark .micro-pill--academic-blue .micro-pill__label {
          color: #93C5FD;
        }

        html.dark .micro-pill--academic-blue .micro-pill__sub {
          color: #60A5FA;
        }

        /* Small screens */
        @media (max-width: 360px) {
          .micro-pill {
            min-width: 120px;
            height: 46px;
            padding: 0 8px;
            gap: 6px;
          }
          .micro-pill__icon {
            width: 30px;
            height: 30px;
          }
          .micro-pill__label {
            font-size: 13px;
          }
          .micro-pill__sub {
            font-size: 11px;
          }
        }
      `}</style>
    </section>
  );
}

export { HubsMiniRow };
